import React, { useState, useEffect } from 'react';
import ServiceAPI from '../../services';
import { toast } from 'react-toastify';
import {
  Card,
  Row,
  Col,
  Image,
  Accordion,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import googleLogo from '../../img/icons-google.png';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Loading from '../Loading/Loading';
import { ReactComponent as IconInstagram } from '../../img/sprite/svg/instagram.svg';
import { ReactComponent as IconRobot } from '../../img/sprite/svg/robot.svg';
import { ReactComponent as IconRefresh } from '../../img/sprite/svg/refresh.svg';
import { ReactComponent as IconConfirm } from '../../img/sprite/svg/confirm.svg';

const SingleReview = ({
  id,
  author,
  rating,
  date,
  snippet,
  googlePlaceId,
  businessResponse,
  businessReplyText,
  editResponse,
  aiAutopilotButtonDissabled,
  downloadReviewModal
}) => {
  const service = new ServiceAPI();
  const { t } = useTranslation();
  const firstLetter = author.charAt(0).toUpperCase();
  const notify = () => toast(t('ReviewsPage.aiAnswerCopied'));
  const notifyPublished = () =>
    toast.success(t('ReviewsPage.aiAnswerPublished'));

  const [open, setOpen] = useState(false);
  const [loadingAiAnswer, setLoadingAiAnswer] = useState(false);
  const [answer, setAnswer] = useState('');
  const [link, setLink] = useState(null);
  const [answerCount, setAnswerCount] = useState(0);
  const [relPubButtonsBlock, setRelPubButtonsBlock] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingAiAnswer(true);
      service.getAiAnswer(id).then((resp) => {
        setAnswer(MarkdownLinkParser(resp.data?.ai_response));
        setLink(resp.data?.google_review_link);
        setLoadingAiAnswer(false);
        setAnswerCount(1);
      });
    }
  }, [open]);

  const onReload = () => {
    setLoadingAiAnswer(true);
    setAnswer('');
    setLink(null);
    service.getAiAnswer(id).then((resp) => {
      setAnswer(MarkdownLinkParser(resp.data?.ai_response));
      setLink(resp.data?.google_review_link);
      setLoadingAiAnswer(false);
      setAnswerCount(answerCount + 1);
    });
  };

  const getBackgroundColor = (name) => {
    const colors = [
      '#FF5722', // Deep Orange
      '#3F51B5', // Indigo
      '#4CAF50', // Green
      '#FFC107', // Amber
      '#9C27B0', // Purple
      '#03A9F4', // Light Blue
      '#E91E63', // Pink
      '#8BC34A' // Light Green
    ];
    const charCode = name.charCodeAt(0);
    return colors[charCode % colors.length];
  };

  const backgroundColor = getBackgroundColor(author);

  const handleDownload = () => {
    downloadReviewModal(
      author,
      rating,
      date,
      snippet,
      backgroundColor,
      firstLetter,
      googlePlaceId
    );
  };

  const handleEditReply = () => {
    setOpen(!open);
  };

  const handleOpenReplyWithAi = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const copyFallback = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const handleCopy = () => {
    try {
      setRelPubButtonsBlock(true);
      service.postAiAnswer({ id, answer }).then((resp) => {
        if (resp.data?.published) {
          notifyPublished();
        } else {
          if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(answer).catch(() => {
              copyFallback(answer);
            });
          } else {
            copyFallback(answer);
          }
          notify();
          window.open(
            link,
            '_blank',
            'width=400,height=700,noopener,noreferrer'
          );
        }
        setRelPubButtonsBlock(false);
      });
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const MarkdownLinkParser = (text) => {
    const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

    if (!regex.test(text)) {
      return text;
    }

    regex.lastIndex = 0;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      parts.push(
        <a
          key={match.index}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[1]}
        </a>
      );

      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    setRelPubButtonsBlock(true);
    return <p>{parts}</p>;
  };

  return (
    <>
      <Card className="review-card mb-4">
        <Card.Body className="p-3">
          <Row className="align-items-center mb-1 d-flex justify-content-between">
            <Col xs="auto" className="pr-0">
              <div
                className="circle text-white d-flex align-items-center justify-content-center"
                style={{ backgroundColor }}
              >
                {firstLetter}
              </div>
            </Col>
            <Col>
              <Card.Title className="mb-0">{author}</Card.Title>
            </Col>
            <Col
              xs="auto"
              className="d-none d-md-flex text-muted align-items-center ms-2"
            >
              {t('ReviewsPage.importedFrom')}:{' '}
              <Image
                src={googleLogo}
                alt="Google logo"
                className="google-logo ms-1"
              />
            </Col>
          </Row>

          <Row className="mb-1">
            <Col>
              {[...Array(5)].map((_, index) => (
                <i
                  key={index}
                  className={`bi ${
                    index < rating
                      ? 'bi-star-fill text-warning'
                      : 'bi-star-fill text-secondary'
                  }`}
                ></i>
              ))}
            </Col>
          </Row>

          <Row className="mb-1">
            <Col>
              <Card.Text>{snippet}</Card.Text>
            </Col>
          </Row>

          {date && (
            <Row className="mb-1 text-muted">
              <Col>{date}</Col>
            </Row>
          )}

          {businessReplyText?.length > 0 && (
            <Row className="mb-2">
              <Col>
                <Card.Text className="business-reply">
                  {businessReplyText}
                </Card.Text>
              </Col>
            </Row>
          )}

          {/* Show only on small screens */}
          <Row className="mb-1 text-muted d-flex d-md-none">
            <Col className="d-flex align-items-center">
              {t('ReviewsPage.importedFrom')}:{' '}
              <Image
                src={googleLogo}
                alt="Google logo"
                className="google-logo ms-1"
              />
            </Col>
          </Row>

          <Row className="d-flex justify-content-between flex-column flex-md-row text-center text-md-start">
            <Col
              xs={12}
              md="auto"
              className="mb-2 mb-md-0 d-flex justify-content-center justify-content-md-start"
            >
              {businessResponse && !editResponse ? (
                <button
                  className="btn-responses btn-share"
                  type="button"
                  disabled
                >
                  <IconConfirm />
                  {t('ReviewsPage.alreadyRepliWithAi')}
                </button>
              ) : businessResponse && editResponse ? (
                <button
                  className="btn-responses btn-share"
                  type="button"
                  onClick={handleEditReply}
                >
                  <IconRobot />
                  {t('ReviewsPage.editResponse')}
                </button>
              ) : (
                <button
                  className="btn-responses btn-share"
                  type="button"
                  onClick={handleOpenReplyWithAi}
                >
                  <IconRobot />
                  {t('ReviewsPage.replyWithAi')}
                </button>
              )}
            </Col>
            <Col
              xs={12}
              md="auto"
              className="d-flex justify-content-center justify-content-md-start"
            >
              <button
                className="btn-responses btn-share"
                type="button"
                onClick={handleDownload}
              >
                <IconInstagram />
                {t('ReviewsPage.share')}
              </button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Accordion activeKey={open ? '0' : null}>
        <Card
          style={{
            border: open ? '1px solid #dee2e6' : 'none',
            overflow: open ? 'visible' : 'hidden',
            position: 'relative'
          }}
          className="reply-card"
        >
          <button
            className="btn-close-circle"
            type="button"
            onClick={handleClose}
            aria-label={t('ReviewsPage.close')}
          >
            ✕
          </button>
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <div>
                {loadingAiAnswer ? (
                  <div className="review-text">
                    <Loading />
                  </div>
                ) : typeof answer === 'string' ? (
                  <textarea
                    className="form-control review-text"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    rows={4}
                  />
                ) : (
                  <div className="review-text">{answer}</div>
                )}
              </div>
              <div className="d-flex flex-column flex-md-row justify-content-center justify-content-md-between align-items-center w-100 mt-3">
                {answerCount > 4 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{t('ReviewsPage.reloadLimit')}</Tooltip>}
                  >
                    <button
                      className="btn-responses btn-refresh mb-2 mb-md-0 me-md-2"
                      type="button"
                      onClick={onReload}
                      disabled
                    >
                      <IconRefresh />
                      {t('ReviewsPage.reload')}
                    </button>
                  </OverlayTrigger>
                ) : (
                  <button
                    className="btn-responses btn-refresh mb-2 mb-md-0 me-md-2"
                    type="button"
                    onClick={onReload}
                    disabled={loadingAiAnswer || relPubButtonsBlock}
                  >
                    <IconRefresh />
                    {t('ReviewsPage.reload')}
                  </button>
                )}
                {aiAutopilotButtonDissabled ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>{t('ReviewsPage.publishTooltip')}</Tooltip>
                    }
                  >
                    <button
                      className="btn-responses btn-publish"
                      type="button"
                      onClick={handleCopy}
                      disabled={loadingAiAnswer || relPubButtonsBlock}
                    >
                      {t('ReviewsPage.publish')}
                    </button>
                  </OverlayTrigger>
                ) : (
                  <button
                    className="btn-responses btn-publish"
                    type="button"
                    onClick={handleCopy}
                    disabled={loadingAiAnswer || relPubButtonsBlock}
                  >
                    {t('ReviewsPage.publish')}
                  </button>
                )}
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </>
  );
};

export default SingleReview;

SingleReview.propTypes = {
  id: PropTypes.number,
  author: PropTypes.string,
  rating: PropTypes.string,
  date: PropTypes.string,
  snippet: PropTypes.string,
  businessResponse: PropTypes.bool,
  businessReplyText: PropTypes.string,
  editResponse: PropTypes.bool,
  aiAutopilotButtonDissabled: PropTypes.bool,
  downloadReviewModal: PropTypes.func
};

SingleReview.defaultProps = {
  id: null,
  author: '',
  rating: null,
  date: null,
  snippet: '',
  businessResponse: false,
  businessReplyText: '',
  editResponse: false,
  aiAutopilotButtonDissabled: true,
  downloadReviewModal: null
};
